/**
 * Support — Authoring Policy Thresholds
 *
 * Shared, named thresholds for support-content quality and coverage. Lifted
 * out of bare literals scattered across `supportCoverageReport.ts` and
 * `governance/pipeline/stages/supportStage.ts` as part of RC Ronde 4
 * (S-10 / S-11 / S-12 / S-13).
 *
 * These are *authoring* heuristics — the audience is the content author, not
 * the runtime. They're calibrated for groep 6/7/8 (9-12 years).
 *
 * If you want to change a number here, also update:
 *   - `docs/specs/SPIEKBRIEFJE_VOICE_UPGRADE_2026.md` §4 (voice guide)
 *   - `docs/audits/SUPPORT_DOMAIN_RC_AUDIT_2026.md` §2.5 / §7 (audit log)
 */

export const SUPPORT_THRESHOLDS = {
  /**
   * Max words per sentence in a hint's `content` body. Drives the
   * `length` issue in `validateHintQuality()`.
   *
   * 14 is the cutoff at which readability for groep 6-8 starts to degrade
   * (matches Dutch CITO-band readability heuristics for L4 leesteksten).
   */
  MAX_WORDS_PER_SENTENCE: 14,

  /**
   * Minimum char-length for `FamilySupportEntry.defaultExplanation`.
   * Anything shorter is almost certainly an incomplete stub.
   * Governance pipeline emits SUPPORT_EXPLANATION_QUALITY with
   * `metadata.reason = 'too_short'` below this.
   */
  MIN_EXPLANATION_CHARS: 20,

  /**
   * Maximum char-length for `defaultExplanation`. Beyond this the kid
   * can't scan it in the FeedbackSplash / cheat-sheet header.
   * Governance pipeline emits SUPPORT_EXPLANATION_QUALITY with
   * `metadata.reason = 'too_long'` above this.
   */
  MAX_EXPLANATION_CHARS: 500,

  /**
   * Minimum number of authored questions per family for "adequate practice
   * coverage". Below this, the governance pipeline emits
   * `SUPPORT_FAMILY_LOW_QUESTION_COUNT` (info-level, not blocking).
   */
  MIN_QUESTIONS_PER_FAMILY: 5,

  /**
   * Soft cap on `bubbleHint` length (chars). Drawer-bubble is one breath;
   * longer than this gets visually awkward on phones. Soft because the
   * UI auto-derives from explanation if no bubbleHint is set.
   */
  MAX_BUBBLE_HINT_CHARS: 120,

  /**
   * Maximum hints per family before the spiekbriefje becomes unwieldy.
   *
   * Since `SubSkillCheatSheet` switched to primary-card + collapsible
   * secondaries (RC Ronde 4 / SPIEKBRIEFJE_VOICE_UPGRADE v1.4), the kid
   * sees 1 expanded + N collapsed strips. Past 5 strips the "Meer hulp"
   * list starts to scroll on phone and the choice-architecture flips from
   * "scannable menu" to "wall of options" — which is exactly the
   * cognitive-load problem the redesign solved.
   *
   * Validator emits a 'count' issue when a family exceeds this. Soft cap
   * — over-budget hints aren't broken, but they signal a candidate for
   * consolidation (often two hints can be merged into one).
   */
  MAX_HINTS_PER_FAMILY: 5,

  /**
   * Minimum citoDifficulty spread (max − min) within a family for the
   * spread to count as "varied enough" for balanced assessment. Below
   * this, supportStage emits SUPPORT_FAMILY_CITO_SPREAD with
   * `metadata.reason = 'low_spread'`. Calibrated against the
   * CITO-band quartile width — 0.10 is roughly one quartile of the
   * 0.10-0.85 valid difficulty range.
   */
  MIN_CITO_SPREAD: 0.1,

  /**
   * Minimum family size before the cito-spread check runs. With fewer
   * than 3 cito-tagged questions the spread number is meaningless
   * (range of 2 points is always the spread, even for "varied" content).
   */
  MIN_FAMILY_SIZE_FOR_CITO_SPREAD: 3,

  /**
   * Minimum family size before the number-pattern and linguistic-template
   * collapse checks run. Below 5 questions, the n-gram / digit-pattern
   * analyses don't have enough signal to distinguish "small family" from
   * "template collapse" — checks are skipped entirely.
   */
  MIN_FAMILY_SIZE_FOR_PATTERN_ANALYSIS: 5,
} as const

export type SupportThreshold = keyof typeof SUPPORT_THRESHOLDS
